/**
 * Garden detail page — shared between v1 and v2.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Edit2, Flower2, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { EntityImageGallery } from "@/components/EntityImageGallery";
import { EditEntityDialog } from "@/components/v2/dialogs/EditEntityDialog";
import { DeleteEntityDialog } from "@/components/v2/dialogs/DeleteEntityDialog";
import type { FieldDef } from "@/components/v2/dialogs/AddEntityDialog";
import { authFetch } from "@/lib/auth";

type Status = "planning" | "active" | "fallow" | "retired";

interface Garden {
  id: string;
  ranch_id: string;
  name: string;
  status: Status | null;
  area_sqft: number | null;
  irrigation_type: string | null;
  soil_type: string | null;
  season_year: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_LABEL: Record<Status, string> = {
  planning: "Planning",
  active: "Active",
  fallow: "Fallow",
  retired: "Retired",
};
const STATUS_TONE: Record<Status, string> = {
  active: "bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark",
  planning: "bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark",
  fallow: "bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark",
  retired: "bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark",
};

const FIELDS: FieldDef[] = [
  { name: "name", label: "Name", required: true, placeholder: "Kitchen garden" },
  {
    name: "status",
    label: "Status",
    type: "select",
    half: true,
    options: (Object.keys(STATUS_LABEL) as Status[]).map((v) => ({
      value: v,
      label: STATUS_LABEL[v],
    })),
  },
  { name: "area_sqft", label: "Area (sq ft)", type: "number", half: true, placeholder: "800" },
  { name: "irrigation_type", label: "Irrigation", half: true, placeholder: "Drip" },
  { name: "soil_type", label: "Soil", half: true, placeholder: "Sandy loam" },
  { name: "season_year", label: "Season year", type: "number", placeholder: "2026" },
  { name: "notes", label: "Notes", type: "textarea" },
];

function transformGarden(v: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (v.name !== undefined) out.name = v.name;
  if (v.status !== undefined) out.status = v.status || "active";
  if (v.area_sqft !== undefined) out.area_sqft = v.area_sqft ? Number(v.area_sqft) : null;
  if (v.irrigation_type !== undefined) out.irrigation_type = v.irrigation_type || null;
  if (v.soil_type !== undefined) out.soil_type = v.soil_type || null;
  if (v.season_year !== undefined) out.season_year = v.season_year ? Number(v.season_year) : null;
  if (v.notes !== undefined) out.notes = v.notes || null;
  return out;
}

function statusOf(g: Garden): Status {
  if (g.status === "planning" || g.status === "active" || g.status === "fallow" || g.status === "retired") {
    return g.status;
  }
  return "active";
}

export function GardenDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [garden, setGarden] = useState<Garden | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const data = await authFetch<Garden>(`/api/gardens/${id}`);
      setGarden(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load garden");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load, refreshTick]);

  if (loading) {
    return (
      <>
        <PageHeader
          title=""
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Gardens", href: "/gardens" },
            { label: "Loading…" },
          ]}
        />
        <Skeleton className="h-48 w-full rounded-lg" />
      </>
    );
  }

  if (error || !garden) {
    return (
      <>
        <PageHeader
          title="Garden Not Found"
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Gardens", href: "/gardens" },
            { label: "Error" },
          ]}
        />
        <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">
          {error || "Garden not found"}
        </div>
      </>
    );
  }

  const status = statusOf(garden);

  return (
    <>
      <PageHeader
        title={garden.name}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Gardens", href: "/gardens" },
          { label: garden.name },
        ]}
      >
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
          <Edit2 className="mr-1 h-4 w-4" /> Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDeleteOpen(true)}
          className="text-destructive border-destructive/30"
        >
          <Trash2 className="mr-1 h-4 w-4" /> Archive
        </Button>
      </PageHeader>

      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Flower2 className="h-5 w-5 text-ranch-green" /> Garden Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="mt-1">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[status]}`}
                  >
                    {STATUS_LABEL[status]}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Area</p>
                <p className="font-medium">
                  {garden.area_sqft != null ? `${garden.area_sqft.toLocaleString()} sq ft` : "—"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Irrigation</p>
                <p className="font-medium capitalize">{garden.irrigation_type ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Soil</p>
                <p className="font-medium capitalize">{garden.soil_type ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Season year</p>
                <p className="font-medium">{garden.season_year ?? "—"}</p>
              </div>
              {garden.notes ? (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{garden.notes}</p>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <EntityImageGallery entityType="garden" entityId={garden.id} />
          </CardContent>
        </Card>
      </div>

      <EditEntityDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title={`Edit ${garden.name}`}
        endpoint="/api/gardens"
        entityId={garden.id}
        fields={FIELDS}
        initialValues={{
          name: garden.name,
          status,
          area_sqft: garden.area_sqft != null ? String(garden.area_sqft) : "",
          irrigation_type: garden.irrigation_type ?? "",
          soil_type: garden.soil_type ?? "",
          season_year: garden.season_year != null ? String(garden.season_year) : "",
          notes: garden.notes ?? "",
        }}
        transform={transformGarden}
        onSaved={() => setRefreshTick((t) => t + 1)}
      />
      <DeleteEntityDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        endpoint="/api/gardens"
        entityId={garden.id}
        entityName={garden.name}
        onDeleted={() => navigate("/gardens")}
      />
    </>
  );
}
